// Helpers puros da aba "Integração por empresa" do provider Correios.

import {
  CORREIOS_BASE_URL,
  CorreiosAmbiente,
  CorreiosIntegration,
  CorreiosIntegrationFormData,
} from '@/types/correiosIntegration';

/** Estado do formulário (tudo string, como o usuário digita). */
export interface CorreiosIntegrationFormState {
  company_id: number;
  enabled: boolean;
  ambiente: CorreiosAmbiente;
  base_url: string;
  rastro_base_url: string;
  usuario: string;
  codigo_acesso_api: string;
  contrato: string;
  cartao_postagem: string;
  dr: string;
  timeout_seconds: string;
  /** Teto de valor declarado em R$ (ex.: "10.000,00"); vazio = sem teto. */
  declared_value_cap: string;
}

export const emptyCorreiosForm = (companyId = 0): CorreiosIntegrationFormState => ({
  company_id: companyId,
  enabled: true,
  ambiente: 'producao',
  base_url: CORREIOS_BASE_URL.producao,
  rastro_base_url: '',
  usuario: '',
  codigo_acesso_api: '',
  contrato: '',
  cartao_postagem: '',
  dr: '72',
  timeout_seconds: '30',
  declared_value_cap: '',
});

/**
 * "10.000,00" → 1000000 (centavos). Aceita "10000", "10000.5", "10.000,5".
 * Vazio → null (sem teto). Inválido → NaN (o chamador valida).
 */
export const centsFromBRL = (input: string): number | null => {
  const s = input.trim();
  if (!s) return null;
  if (s.includes('-')) return NaN;
  // com vírgula: separador decimal brasileiro; pontos são milhar
  const normalized = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s;
  const cleaned = normalized.replace(/[^\d.]/g, '');
  if (!/\d/.test(cleaned)) return NaN;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return NaN;
  return Math.round(value * 100);
};

/** 1000000 → "10.000,00"; null/undefined → "". */
export const brlFromCents = (cents?: number | null): string => {
  if (cents == null) return '';
  return (cents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const formFromIntegration = (row: CorreiosIntegration): CorreiosIntegrationFormState => ({
  company_id: row.company_id,
  enabled: row.enabled,
  ambiente: row.ambiente,
  base_url: row.base_url,
  rastro_base_url: row.rastro_base_url ?? '',
  usuario: row.usuario,
  codigo_acesso_api: row.codigo_acesso_api,
  contrato: row.contrato,
  cartao_postagem: row.cartao_postagem,
  dr: row.dr != null ? String(row.dr) : '',
  timeout_seconds: String(row.timeout_seconds),
  declared_value_cap: brlFromCents(row.declared_value_cap_cents),
});

/**
 * Validação local (a definitiva é do backend, em domain.ValidateCorreiosIntegration).
 * Devolve o payload pronto ou a mensagem do primeiro problema.
 */
export const buildCorreiosPayload = (
  f: CorreiosIntegrationFormState,
): { payload?: CorreiosIntegrationFormData; error?: string } => {
  if (!f.company_id) return { error: 'Selecione a empresa' };
  if (!f.usuario.trim()) return { error: 'Informe o usuário (login Meu Correios)' };
  if (!f.codigo_acesso_api.trim()) return { error: 'Informe o código de acesso à API (gerado no portal CWS)' };
  const contrato = f.contrato.replace(/\D/g, '');
  if (!contrato) return { error: 'Informe o número do contrato' };
  const cartao = f.cartao_postagem.replace(/\D/g, '');
  if (!cartao) return { error: 'Informe o cartão de postagem' };

  let dr: number | null = null;
  if (f.dr.trim()) {
    if (!/^\d{1,2}$/.test(f.dr.trim())) return { error: 'DR deve ser um número de até 2 dígitos (SP = 72)' };
    dr = Number(f.dr.trim());
  }
  let timeout: number | null = null;
  if (f.timeout_seconds.trim()) {
    if (!/^\d+$/.test(f.timeout_seconds.trim())) return { error: 'Timeout deve ser um número inteiro de segundos' };
    timeout = Number(f.timeout_seconds.trim());
  }
  const capCents = centsFromBRL(f.declared_value_cap);
  if (Number.isNaN(capCents)) return { error: 'Teto de valor declarado inválido (ex.: 10.000,00)' };

  return {
    payload: {
      enabled: f.enabled,
      ambiente: f.ambiente,
      base_url: f.base_url.trim() || CORREIOS_BASE_URL[f.ambiente],
      rastro_base_url: f.rastro_base_url.trim() || null,
      usuario: f.usuario.trim(),
      codigo_acesso_api: f.codigo_acesso_api.trim(),
      contrato,
      cartao_postagem: cartao,
      dr,
      timeout_seconds: timeout,
      declared_value_cap_cents: capCents,
    },
  };
};

/** Troca de usuário/código/contrato/cartão/ambiente/host zera o token no backend. */
export const credentialsWillResetToken = (
  original: CorreiosIntegration | null,
  f: CorreiosIntegrationFormState,
): boolean => {
  if (!original) return false;
  return (
    original.usuario !== f.usuario.trim() ||
    original.codigo_acesso_api !== f.codigo_acesso_api.trim() ||
    original.contrato !== f.contrato.replace(/\D/g, '') ||
    original.cartao_postagem !== f.cartao_postagem.replace(/\D/g, '') ||
    original.ambiente !== f.ambiente ||
    original.base_url.replace(/\/+$/, '') !== (f.base_url.trim() || CORREIOS_BASE_URL[f.ambiente]).replace(/\/+$/, '')
  );
};

export const formatCNPJ = (doc?: string | null): string => {
  const d = (doc ?? '').replace(/\D/g, '');
  if (d.length !== 14) return doc ?? '';
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};
